import { useRef } from "react";

/**
 * Contains polylineRef and currentPolylineRef, and functions to manipulate these on the map
 * 
 * these lines are references to the google map polylines, and are not the same as lines (usePoiService) and currentLine (usePoiMap)
 * 
 * polylineRef - contains references to all lines on the map that are not currentLine
 * currentPolylineRef - contains reference to currentLine on the map
 * 
 * used refs for these because google map polylines are difficult to remove from the map without calling google map api directly
 * 
 * TODO: figure out if it is necessary to have currentLine and currentPolylineRef, and lines and polylineRef - redundant?
 * 
 * @param {React.RefObject<HTMLDivElement>} mapRef - ref for the map instance in MapComponent, add lines directly to this
 * 
 * @returns {Function} addPolylines - add all (red) lines to the map
 * @returns {Function} clearPolylines - clear all (red) lines from the map
 * @returns {Function} addCurrentPolyline - add current (green) polyline to the map
 * @returns {Function} clearCurrentPolyline - clear current (green) polyline from map
 * 
 * @category Hooks - POI Map
 */
const usePolylineManager = (mapRef) => {
    const polylineRef = useRef([]);
    const currentPolylineRef = useRef({}); //made this an object because there should only ever be one currentLine - important to clear this properly when currentLine changes

    const addPolylines = (lines) => {
        lines.forEach(line => {
            const polyline = new window.google.maps.Polyline({
                path: [line.start, line.end],
                map: mapRef.current,
                strokeColor: '#FF0000',
                strokeWeight: 2,
            });
            polylineRef.current.push(polyline);
        });
    };

    const clearPolylines = () => {
        polylineRef.current.forEach(polyline => polyline.setMap(null));
        polylineRef.current = [];
    };

    const addCurrentPolyline = (newLine) => {
        const newPolyline = new window.google.maps.Polyline({
            path: [newLine.start, newLine.end],
            strokeColor: '#00FF00',
            strokeWeight: 2,
        });
        newPolyline.setMap(mapRef.current);
        currentPolylineRef.current = newPolyline
    };

    const clearCurrentPolyline = () => {
        if(Object.keys(currentPolylineRef.current).length === 0) return
        
        //set remove polyline from map
        currentPolylineRef.current.setMap(null)

        //remove polyline from ref
        currentPolylineRef.current = {};
    };

    return {
        addPolylines,
        clearPolylines,
        addCurrentPolyline,
        clearCurrentPolyline,
    };
};

export default usePolylineManager;
